import React from 'react';
import { useSelector } from 'react-redux';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { ClientAccessSettingsProps } from '../../../../ClientAccess.types';
import { OnboardingWorkflow } from '../../../../../CommonSelectors/CommonArea.types';
import { Setting } from '../../components/Setting/Setting';
import {
  dashboardAccessWorkflowPresetsSelector,
  showFinancialPlanningSelector,
} from '../../../../../AdviserArea/AdviserAreaSelector';
import { AutoAccounts } from './components/AutoAccounts/AutoAccounts';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';

export const Onboarding: React.FC<ClientAccessSettingsProps> = ({
  onboardingWorkflow,
  dashboardAccess,
  taxableAccountModelGroup,
  nonTaxableAccountModelGroup,
  completedOnboardingMessage,
  onboardingWorkflowOptions,
  agreementOptions,
  selectedAgreements,
  isLoadingAgreementOptions,
  showBeFi20,
  onChange,
}) => {
  const dashboardAccessWorkflowPresets = useSelector(dashboardAccessWorkflowPresetsSelector);
  const showFinancialPlanning = useSelector(showFinancialPlanningSelector);

  // If financial planning is enabled -> choose Retirement as the default workflow
  // If not -> choose the first one available
  const defaultOnboardingWorkflow =
    showFinancialPlanning &&
    onboardingWorkflowOptions.find(opt => opt.value === OnboardingWorkflow.RetirementWorkflow)
      ? OnboardingWorkflow.RetirementWorkflow
      : onboardingWorkflowOptions[0].value;

  const onOnboardingWorkflowChange = (workflow: OnboardingWorkflow | null) => {
    onChange({
      onboardingWorkflow: workflow,
      dashboardAccess:
        workflow !== null && dashboardAccessWorkflowPresets[workflow]
          ? dashboardAccessWorkflowPresets[workflow].map(({ DashboardAccess: id }) => id)
          : dashboardAccess,
    });
  };

  const showBeFi20Legal =
    showBeFi20 &&
    (onboardingWorkflow === OnboardingWorkflow.BeFi20Workflow ||
      onboardingWorkflow === OnboardingWorkflow.AllWorkflows);

  return (
    <section>
      <Toggle
        variant="setting"
        checked={onboardingWorkflow !== null}
        onChange={evt => {
          onOnboardingWorkflowChange(evt.target.checked ? defaultOnboardingWorkflow : null);
        }}
        my="16"
      >
        Onboarding
      </Toggle>

      {onboardingWorkflow !== null && (
        <Box as="section" pl="24">
          <Setting label="Data Workflow" id="select--ca-onboarding-workflow">
            <Select
              id="ca-onboarding-workflow"
              value={onboardingWorkflowOptions.find(({ value }) => {
                const workflow =
                  onboardingWorkflow !== null ? onboardingWorkflow : defaultOnboardingWorkflow;

                return value === workflow;
              })}
              options={onboardingWorkflowOptions}
              onChange={opt => {
                if (opt) {
                  onOnboardingWorkflowChange(opt.value);
                }
              }}
            />
          </Setting>
        </Box>
      )}

      {onboardingWorkflow === OnboardingWorkflow.AutoAccountWorkflow && (
        <Box as="section" pl="48">
          <AutoAccounts
            taxableAccountModelGroup={taxableAccountModelGroup}
            nonTaxableAccountModelGroup={nonTaxableAccountModelGroup}
            agreementOptions={agreementOptions}
            selectedAgreements={selectedAgreements}
            isLoadingAgreementOptions={isLoadingAgreementOptions}
            onChange={onChange}
          />
        </Box>
      )}

      {showBeFi20Legal && (
        <Text as="p" fontSize="small" color="neutral.500" pl="24" my="16">
          You understand and agree that the BeFi20 assessment is designed to be shareable in nature.
          By sharing and/or providing information via the assessment with your clients, prospective
          clients, or otherwise, you understand, agree, and consent to such information and results
          being shared (in the discretion of such individuals who interact with the assessment) with
          the individual(s) who you share this assessment with, the individuals who those
          individuals share this assessment with (and potentially such individuals&apos; financial
          advisors and service providers), and the like. By proceeding, you are consenting to such
          activity on your behalf and on the behalf of your firm.
        </Text>
      )}

      {completedOnboardingMessage && (
        <Text as="p" my="16">
          <Icon className="fas fa-check-circle" color="success.500" mr="4" />
          {completedOnboardingMessage}
        </Text>
      )}
    </section>
  );
};
